<template>
  <v-card class="my-2" v-if="forms.length">
    <v-card-text>
      <div class="text-subtitle-1 font-weight-bold mb-2">Additional forms</div>
      <div v-for="item in forms" :key="item.surveyId" class="mb-3">
        <div class="d-flex flex-wrap align-center justify-space-between">
          <div>
            <div class="font-weight-medium">{{ item.surveyTitle }}</div>
            <div class="text-caption text-medium-emphasis" v-if="item.alreadySubmitted">Submitted</div>
          </div>
          <v-btn
            v-if="!item.alreadySubmitted"
            size="small"
            color="primary"
            class="mt-2"
            @click="openFill(item)"
          >
            Fill form
          </v-btn>
        </div>
      </div>
    </v-card-text>
  </v-card>

  <simple-dialog
    ref="dlg"
    :title="activeForm?.surveyTitle || 'Additional form'"
    width="700"
    :close-on-save="false"
    @ok="submitFill"
  >
    <template v-slot:content>
      <SurveyQuestionsForm
        v-if="activeForm"
        ref="formRef"
        :questions="activeForm.questions || []"
        :description="activeForm.description || ''"
        :allow-files="true"
      />
    </template>
  </simple-dialog>
</template>

<script setup>
import { ref, inject, onMounted, watch } from "vue";
import {
  getMiqaatSurveysForMember,
  getRegistrationSurveyForms,
} from "@/services/miqaatService";
import { addDetail, addFiles } from "@/services/surveyService";
import SurveyQuestionsForm from "@/components/SurveyQuestionsForm.vue";

const props = defineProps({
  miqaatId: { type: [Number, String], required: true },
});

const loader = inject("loader");
const snackbar = inject("snackBar");

const forms = ref([]);
const dlg = ref(null);
const formRef = ref(null);
const activeForm = ref(null);

async function loadForms() {
  if (!props.miqaatId) return;
  try {
    forms.value = (await getMiqaatSurveysForMember(props.miqaatId)) || [];
  } catch {
    forms.value = [];
  }
}

async function openFill(item) {
  loader.show();
  try {
    const list = await getRegistrationSurveyForms(props.miqaatId, "AfterRegistration");
    activeForm.value = (list || []).find((x) => x.surveyId === item.surveyId) || null;
    if (!activeForm.value) {
      snackbar.error("Unable to load form questions");
      return;
    }
    dlg.value.show();
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

async function submitFill() {
  const collected = formRef.value?.collectAnswers();
  if (!collected) return;

  loader.show();
  try {
    if (collected.files?.length) {
      await addFiles(activeForm.value.surveyId, collected.files);
    }
    await addDetail(activeForm.value.surveyId, collected.answers);
    snackbar.success("Form submitted successfully");
    dlg.value.hide();
    await loadForms();
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

watch(() => props.miqaatId, loadForms);
onMounted(loadForms);

defineExpose({ reload: loadForms });
</script>
